/**
 * Color theme presets — professional palette set.
 * primary / accent are HSL triples (no "hsl()" wrapper) so they can be used
 * directly as Tailwind CSS variable values.
 */
export interface ColorPreset {
  id: string;
  name: string;
  description: string;
  /** HSL triple, e.g. "211 100% 35%" — used for CSS variable injection */
  primary: string;
  primaryHover?: string;
  accent: string;
  /** Optional override for --foreground (text colour). Pure dark when omitted by global default. */
  foreground?: string;
  /** Secondary color for muted text/elements */
  secondary?: string;
  /** If true, also applies the `.dark` class to <html> */
  dark?: boolean;
  /**
   * Optional Google Font name to load and apply as the body font for this theme.
   * When set, `applyColorPreset` injects a <link> for the font and sets
   * `--theme-font-family` on :root.
   * e.g. "DM Sans" for the 301io theme.
   */
  fontFamily?: string;
  /** Optional CSS border-radius value for buttons/cards (e.g. "9999px" for full pill). */
  buttonRadius?: string;
  /** Logo image path for this brand theme */
  logoPath?: string;
  /** Brand tagline / slogan */
  tagline?: string;
  /** Company/brand name for document title (e.g., "301io", "BrandsMart USA") */
  brandName?: string;
  /** Product name to display in UI (e.g., "HomeIQ" for 301io, "BrandsMart" for BrandsMart) */
  productName?: string;
  /** Deal badge name for promotions (e.g., "HOMEIQ DEAL", "BRANDSMART DEAL") */
  dealBadgeName?: string;
  /** Deal provider name for display (e.g., "HomeIQ", "BrandsMart") */
  dealProviderName?: string;
}

export const COLOR_PRESETS: ColorPreset[] = [
  {
    id: "301io",
    name: "301io",
    description: "Digital transformation experts with modern magenta branding",
    // Exact 301io.com brand magenta: #E20074 → HSL 329° 100% 44%
    primary: "329 100% 44%",
    primaryHover: "329 100% 36%",
    // 301io uses black as accent (from website)
    accent: "0 0% 7%",
    // Near-black body text on white backgrounds
    foreground: "0 0% 7%",
    // Secondary grey for subtle text (from website)
    secondary: "215 16% 47%",
    // 301io.com signature font
    fontFamily: "DM Sans",
    // 301io.com uses full pill-shaped buttons
    buttonRadius: "9999px",
    logoPath: "HomeIQ-General-Logo.png",
    tagline: "Digital Transformation Experts",
    brandName: "301io",
    productName: "301io",
    dealBadgeName: "301io DEAL",
    dealProviderName: "301io",
  },
  {
    id: "brandsmart",
    name: "301io",
    description: "Home protection and services with trusted blue branding",
    primary: "211 100% 35%",
    primaryHover: "211 100% 28%",
    accent: "348 83% 47%",
    foreground: "211 100% 35%",
    secondary: "215 16% 47%",
    fontFamily: "Inter",
    buttonRadius: "0.5rem",
    // Use relative path from public folder
    logoPath: "BrandsMartUSA_Logo.png",
    tagline: "Home Protection & Services",
    brandName: "301io",
    productName: "301io",
    dealBadgeName: "301io DEAL",
    dealProviderName: "301io",
  },
];

export function getPresetById(id: string): ColorPreset {
  return COLOR_PRESETS.find((p) => p.id === id) ?? COLOR_PRESETS[0];
}

/** Default theme ID - 301io is now the default */
export const DEFAULT_THEME_ID = "301io";
